import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";
import LoadingIndicator from "../Components/LoadingIndicator";
import { PERMISSIONS } from "../Components/Permission";

const Role = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const navigate = useNavigate();

  useEffect(() => {
    fetchRoles();
  }, [currentPage, appliedSearch, limit, sortBy, sortOrder]);

  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "getRole",
        page: String(currentPage),
        limit: String(limit),
        status: "active",
        search: appliedSearch,
        sortBy,
        sortOrder,
      };

      if (appliedSearch.trim()) {
        payload.search = appliedSearch.trim();
      }

      const response = await axios.post(API_URL, payload);

      const data = response.data;

      setRows(data.roles || data.data || []);
      setTotalPages(data.pagination?.totalPages || data.totalPages || 1);
      setTotalCount(data.pagination?.total || data.totalCount || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load roles");
    } finally {
      setIsLoading(false);
    }
  };

  const capitalizeWords = (text) => text?.toUpperCase();

  const handleDeleteRole = async () => {
    const userId = localStorage.getItem("userid");

    setIsDeleting(true);
    try {
      await axios.post(API_URL, {
        action: "deleteRole",
        id: selectedRow?.id,
        updatedBy: userId,
      });

      toast.success("Role deleted");
      setIsDeleteOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || "Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "active") return "status-badge active";
    if (s === "inactive") return "status-badge inactive";
    return "status-badge neutral";
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setCurrentPage(1);
  };
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return date.toLocaleString("en-IN");
  };

  const handleSort = (column) => {
    if (sortBy !== column) {
      setSortBy(column);
      setSortOrder("asc");
    } else {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    }

    setCurrentPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <i
          className="fa-solid fa-sort"
          style={{
            marginLeft: 5,
            opacity: 0.5,
            fontSize: 11,
          }}
        />
      );
    }

    return sortOrder === "asc" ? (
      <i
        className="fa-solid fa-sort-up"
        style={{
          marginLeft: 5,
          fontSize: 11,
        }}
      />
    ) : (
      <i
        className="fa-solid fa-sort-down"
        style={{
          marginLeft: 5,
          fontSize: 11,
        }}
      />
    );
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));

    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="d-flex align-items-center">
          <span className="mr-2">Show</span>

          <select
            className="form-control"
            style={{ width: "80px" }}
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <span className="ml-2 mr-3">Entries</span>

          <span className="pagination-info">
            Showing {rows.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{" "}
            {Math.min(currentPage * limit, totalCount)} of {totalCount} entries
          </span>
        </div>

        <div className="pagination-controls">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {pages.map((page) => (
            <button
              key={page}
              className={`pagination-btn ${
                currentPage === page ? "active" : ""
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="fas fa-angle-right"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Role</h1>
            <p>View role details.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/role-create")}
            >
              Add Role
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          <div className="row mb-3 align-items-end">
            <div className="col-xl-5 col-lg-6 col-md-8 col-sm-12 mb-2">
              <form onSubmit={handleSearch}>
                <label className="font-weight-bold mb-1">Search</label>

                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <div className="input-group-append">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      <i className="fas fa-search"></i>
                    </button>

                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClearSearch}
                        disabled={isLoading}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No role data found.</div>
          ) : (
            <>
              <div className="employee-table-wrapper">
                <table className="table table-borderless employee-table subscription-table">
                  <thead>
                    <tr>
                      <th style={{ width: "10%" }}>Sl No.</th>
                      <th
                        style={{ width: "20%", cursor: "pointer" }}
                        onClick={() => handleSort("role")}
                      >
                        Role {renderSortIcon("role")}
                      </th>
                      <th style={{ width: "40%" }}>Access</th>
                      <th
                        style={{ width: "15%", cursor: "pointer" }}
                        onClick={() => handleSort("status")}
                      >
                        Status {renderSortIcon("status")}
                      </th>{" "}
                      <th style={{ width: "15%" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row.id || idx}>
                        <td>{(currentPage - 1) * limit + idx + 1}</td>
                        <td>{capitalizeWords(row.role) || "-"}</td>
                        <td>
                          {Array.isArray(row.permissions) &&
                          row.permissions.length > 0 ? (
                            <>
                              {(expandedRow === idx
                                ? row.permissions
                                : row.permissions.slice(0, 3)
                              ).map((permKey, i) => {
                                const found = PERMISSIONS.find(
                                  (p) => p.key === permKey,
                                );
                                return (
                                  <span
                                    key={i}
                                    style={{
                                      display: "inline-block",
                                      background: "#e6f4ea",
                                      color: "#1e7e34",
                                      padding: "4px 10px",
                                      borderRadius: "20px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      margin: "2px",
                                    }}
                                  >
                                    {(found
                                      ? found.label
                                      : permKey
                                    ).toUpperCase()}
                                  </span>
                                );
                              })}

                              {row.permissions.length > 3 && (
                                <span
                                  onClick={() =>
                                    setExpandedRow(
                                      expandedRow === idx ? null : idx,
                                    )
                                  }
                                  style={{
                                    fontSize: "12px",
                                    color: "#007bff",
                                    marginLeft: "5px",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                  }}
                                >
                                  {expandedRow === idx
                                    ? " Show Less"
                                    : ` +${row.permissions.length - 3} more`}
                                </span>
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <span className={getStatusClass(row.status)}>
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <div className="employee-actions">
                            <button
                              className="action-icon-btn edit"
                              title="Edit"
                              onClick={() => navigate(`/role-edit/${row.id}`)}
                            >
                              <i className="fa-solid fa-pen"></i>
                            </button>

                            <button
                              className="action-icon-btn delete"
                              title="Delete"
                              onClick={() => {
                                setSelectedRow(row);
                                setIsDeleteOpen(true);
                              }}
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {isDeleteOpen && (
        <div className="crop-overlay">
          <div className="crop-modal confirm-modal">
            <div className="crop-header">
              <h5>Delete Role</h5>
              <button
                className="crop-close"
                onClick={() => setIsDeleteOpen(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="crop-body">
              <p>Are you sure want to delete the {selectedRow?.role} role?</p>
            </div>

            <div className="crop-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setIsDeleteOpen(false)}
              >
                Cancel
              </button>
              <button
                className="btn confirm-delete-btn"
                onClick={handleDeleteRole}
              >
                {isDeleting ? <LoadingIndicator size={22} /> : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Role;
